import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import OtpField from "./OtpField";

/** Forgot-password flow: phone → OTP → new password. */
export const PhoneResetDialog = ({ open, onOpenChange, initialPhone = "" }) => {
  const [phone, setPhone] = useState(initialPhone);
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [newPw, setNewPw] = useState("");
  const [step, setStep] = useState("phone");
  const [busy, setBusy] = useState(false);

  const sendOtp = async () => {
    if (!phone) return toast.error("Enter your mobile number");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/request-otp", { phone, purpose: "reset" });
      if (data.demo_otp) setDemoOtp(data.demo_otp);
      setStep("code");
      toast.success("If the number is registered, an OTP has been sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!otp || !newPw) return toast.error("Enter OTP and new password");
    setBusy(true);
    try {
      await api.post("/auth/phone/reset-password", { phone, otp_code: otp, new_password: newPw });
      toast.success("Password updated. Please log in with your new password.");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="phone-reset-dialog">
        <DialogHeader>
          <DialogTitle>Reset password with phone OTP</DialogTitle>
          <DialogDescription>
            {step === "phone"
              ? "Enter the mobile number tied to your account. We'll send a 6-digit code."
              : "Enter the OTP and choose a new password."}
          </DialogDescription>
        </DialogHeader>
        {step === "phone" ? (
          <div className="space-y-4">
            <div>
              <Label>Mobile number</Label>
              <Input data-testid="phone-reset-phone" value={phone}
                     onChange={(e) => setPhone(e.target.value)} placeholder="Your mobile number" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={sendOtp} disabled={busy} className="bg-primary text-primary-foreground"
                      data-testid="phone-reset-send-otp">
                {busy ? "Sending…" : "Send OTP"}
              </Button>
            </div>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <OtpField label="OTP" value={otp} onChange={setOtp} demoOtp={demoOtp}
                      placeholder="123456" testId="phone-reset-otp" demoTestId="phone-reset-demo-otp" />
            <div>
              <Label>New password</Label>
              <Input data-testid="phone-reset-newpw" type="password" minLength={6}
                     value={newPw} onChange={(e) => setNewPw(e.target.value)} required
                     placeholder="At least 6 characters" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("phone")}>Back</Button>
              <Button type="submit" disabled={busy} className="bg-primary text-primary-foreground"
                      data-testid="phone-reset-submit">
                {busy ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhoneResetDialog;
